import {
  ELayoutPosition,
  EScreenType,
  IScreenDesignDto,
  IScreenDesignInfo,
  IScreenFolder
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { MasterLayout } from "../../components/design-tools/left-panel/screens-panel/screen-template-layout";
import { IScreenDesignContextState } from "../../contexts/screen-design-context/screen-design-context";
import { ScreenService } from "../../services/screen-service";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

const DEFAULT_LAYOUT =
  '{"id":-1,"itemType":1,"styleObj":{"fill":{"fillIds":["b469ca57-a59d-48ae-847f-592fc99a7b99"],"fillId": null,"repeatSwitch": false,"bgImageSize": false,"stretch": false,"bgImagePosition": "left"}},"item":{"id":"HTML_DIV","properties":[{"name":"style","selectedType":2,"value":""}]},"children":[],"screenDesign":"[Circular ~]"}';

function isSuccessfulResponse(res: AxiosResponse | null | undefined): boolean {
  return !!res && res.status >= 200 && res.status < 300;
}

/**
 * Collision-detection key for screen-name uniqueness. Strips ALL
 * whitespace and lowercases — designed so PascalCase ("BodyMeasurementInput")
 * and a space-separated variant ("Body Measurement Input") collide. The
 * BRAID intent label and the LLM-picked PascalCase id often disagree on
 * spacing, and a Designer bounce-back rerun can pick the spaced variant
 * without realizing it maps to the same conceptual screen — which lands
 * a duplicate screen because the prior comparison was case-insensitive
 * but whitespace-sensitive.
 *
 * Used ONLY for collision detection. The original `screenName` string is
 * still passed verbatim to the create request — we don't rewrite the
 * caller's chosen name, just refuse a colliding new one.
 */
function normalizeScreenNameForCollision(name: string): string {
  return name.replace(/\s+/g, "").toLowerCase();
}

/**
 * Builds the teaching error returned when `ui_screen.create` is called
 * for a screen that already exists. The order of the strategies matters
 * — multi-screen builds where the user listed THIS exact name (e.g.
 * "Create the following screens: HomePage, GroupsPage, …") should default
 * to navigating into the existing screen and editing it in place. The
 * old wording labeled "invent a different PascalCase name" as PREFERRED,
 * which made the LLM produce SplitMateHome / ExpenseGroups / etc.
 * instead of touching the named screens.
 */
/**
 * KOA-18173 — the "already exists" refusal now carries the colliding screen's
 * id as a STRUCTURED field beside the teaching prose.
 *
 * The id was always in the message text, but a caller that needed it had to
 * regex it back out of a paragraph written for an LLM — prose that is free to
 * be reworded at any time. mcp-chat-server's screen-id resolution needs the
 * id (a screen it did not create is still a screen it is building), so give it
 * a field to read instead of a sentence to parse. The `error` string is
 * unchanged, so the LLM-facing behaviour is identical.
 */
export interface ScreenAlreadyExistsError {
  error: string;
  existingScreenId: string;
}

function buildScreenAlreadyExistsError(screenName: string, existingScreenId: string): string {
  return (
    `Screen "${screenName}" already exists (screenId: ${existingScreenId}). ` +
    `Pick the strategy that matches the user's intent:\n\n` +
    `(a) PREFERRED when the user listed THIS exact name in the request ` +
    `(typical for multi-screen builds, e.g. "Create the following screens: ` +
    `HomePage, GroupsPage, …"): switch to the existing screen with ` +
    `ui_screen.navigate({ screenId: "${existingScreenId}" }) and edit its ` +
    `contents in place. The user wants the screen at this name; do not ` +
    `invent an alternate name.\n\n` +
    `(b) When the user wants a fresh rebuild of THIS exact name and the ` +
    `current contents must be preserved aside (e.g. "rebuild the Login ` +
    `screen from scratch"): first rename the existing screen with ` +
    `ui_screen.rename (newName: "${screenName}_legacy_<YYYY>_<MM>_<DD>", ` +
    `using ONLY letters, digits, underscores, and spaces — parentheses, ` +
    `dashes, or other punctuation make the rename silently fail), then ` +
    `retry ui_screen.create with the original name.\n\n` +
    `(c) LAST RESORT — only when the user did NOT specify this name and ` +
    `the create was your own choice: retry ui_screen.create with a ` +
    `DIFFERENT semantically distinct PascalCase name (e.g. "ModernSignIn", ` +
    `"OnboardingSignIn"). No bare numeric suffixes like "SignIn2"; ` +
    `letters/adjectives only, no spaces, punctuation, or dashes. ` +
    `Do NOT default to (c) when the user wrote this exact name in their ` +
    `request — that's the (a) path.`
  );
}

/**
 * One deadline for "this screen finished loading" — shared by the readiness poll
 * and the project-load settle below, because they are waiting on the same load.
 *
 * A ceiling is required, not optional: `openProject` skips its `callback()`
 * outright when either fetch comes back empty, so an unconditional await would
 * hang the tool on exactly the runs that are already failing.
 */
const SCREEN_LOAD_TIMEOUT_MS = 15000;

async function waitForScreenReady(
  sdInstance: ScreenDesignInstance,
  targetSdId: string,
  oldDictRef: IScreenDesignContextState["screenDesignDictionary"],
  timeoutMs: number = SCREEN_LOAD_TIMEOUT_MS
): Promise<boolean> {
  return new Promise((resolve) => {
    const pollInterval = 100;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += pollInterval;
      const current = getValues(sdInstance);

      if (
        current?.sdId === targetSdId &&
        current?.screenDesignDictionary !== oldDictRef &&
        current?.screenDesignDictionary &&
        Object.keys(current.screenDesignDictionary).length > 0
      ) {
        clearInterval(timer);
        resolve(true);
        return;
      }

      if (elapsed >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, pollInterval);
  });
}

function getScreenInfo(sd: IScreenDesignContextState, screenId: string): IScreenDesignInfo | undefined {
  return (sd.screenList || []).find((screen: IScreenDesignInfo) => String(screen.id) === String(screenId));
}

function syncDuplicatedScreenCanvasSize(
  sd: IScreenDesignContextState,
  sourceScreenId: string,
  targetScreenId: string
): void {
  const project = sd?.project;
  const originalScreenCanvasSize = project?.screenCanvasSizes?.screens?.find((screen) => screen.id === sourceScreenId);
  if (!project || !originalScreenCanvasSize || !sd.updateProject) return;

  if (!project.screenCanvasSizes) {
    project.screenCanvasSizes = { screens: [] } as IScreenDesignContextState["project"]["screenCanvasSizes"];
  }
  if (!project.screenCanvasSizes.screens) {
    project.screenCanvasSizes.screens = [];
  }

  project.screenCanvasSizes.screens.push({
    ...originalScreenCanvasSize,
    id: targetScreenId
  });
  sd.updateProject(project);
}

function syncDuplicatedScreenFolder(
  sd: IScreenDesignContextState,
  sourceScreenId: string,
  targetScreenId: string
): void {
  const folder = sd?.project?.screenFolders?.find((item: IScreenFolder) =>
    item.screenIds?.find((screenId: string) => screenId === sourceScreenId)
  );
  if (folder) {
    sd.addScreenToFolder?.(folder.id, targetScreenId);
  }
}

function buildBlankScreenRequest(projectId: string, screenName: string): IScreenDesignDto {
  const screen: IScreenDesignDto = {
    name: screenName,
    kProject_Id: projectId,
    json: `{"projectId":"${projectId}","approveInfo":null,"id":"@SDID","name":"${screenName}","displayItems":[${DEFAULT_LAYOUT}],"versionNumber":0,"devices":${JSON.stringify(
      { desktop: true, tablet: true, phone: true }
    )}}`,
    publicJson: JSON.stringify({
      screenType: EScreenType.Default,
      screenInputs: [],
      screenOutputs: []
    })
  } as IScreenDesignDto;

  return screen;
}

function buildMasterScreenRequest(projectId: string, screenName: string): IScreenDesignDto {
  const screen: IScreenDesignDto = {
    name: screenName,
    kProject_Id: projectId,
    json: `{"projectId":"${projectId}","approveInfo":null,"id":"@SDID","name":"${screenName}","displayItems":[${MasterLayout}],"versionNumber":0,"devices":${JSON.stringify(
      { desktop: true, tablet: true, phone: true }
    )}}`,
    publicJson: JSON.stringify({
      screenType: EScreenType.Master,
      screenInputs: [],
      screenOutputs: []
    })
  } as IScreenDesignDto;

  return screen;
}

// `reload` defaults to FALSE so the ordinary navigate/create callers
// (openDesignerScreen, createBlankDesignerScreen) keep their fast-path
// behaviour: loadScreen early-returns when the canvas is already on the
// target screen (screen-provider.tsx: `if (this.state.sdId !== id || reload)`),
// avoiding a redundant project re-fetch. Pass `reload=true` ONLY when the
// caller deliberately wants to FORCE a fresh re-fetch + repaint of the
// screen the canvas is already on (reloadDesignerScreen below) — otherwise
// the same-screen reload is a silent no-op and the canvas never repaints.
// onProjectLoaded fires from openProject's callback, which runs AFTER loadProject's
// setState({ project }) lands. Only callers that must observe the freshly-loaded
// project (e.g. duplicate, to re-index StartProcess triggers before the late project
// setState clobbers them) pass it; everyone else keeps the screen-ready-only behaviour.
async function loadScreenAndWait(
  sdInstance: ScreenDesignInstance,
  screenId: string,
  reload: boolean = false,
  onProjectLoaded?: () => void
): Promise<boolean> {
  const sd = getValues(sdInstance);
  const oldDictRef = sd.screenDesignDictionary;
  await sd.loadScreen?.(screenId, reload, true, onProjectLoaded);
  return waitForScreenReady(sdInstance, screenId, oldDictRef);
}

/**
 * Load a screen and return only once the PROJECT load behind it has settled.
 *
 * `waitForScreenReady` is satisfied by the FIRST `setScreenDesign(LoadProject)`
 * (project-provider `openProject`, right after the two fetches) — it sees `sdId`
 * plus a fresh dictionary and reports ready. But `openProject` runs its whole body
 * inside a NON-awaited `processWithSpinner`, so `await loadScreen(...)` returns
 * immediately and `loadProject` is still going: screen list, micro-apps, component
 * set, widget projects, `migrateSdWithCurrentComponents`, the master screen. Only
 * after all of that does it `setState({ screenList, project }, () =>
 * setScreenDesign(screenDesign, LoadProject))` — several HTTP round trips later.
 *
 * That late landing does two things to anything written in the meantime:
 * it REPLACES `screenList` (dropping the entry `applyPublicMetaData` patched) and
 * it RE-SEEDS `ownPublicMetaData` from the blob fetched BEFORE the write — and
 * `resolvePublicMetaData` prefers `ownPublicMetaData`. Screen inputs and outputs
 * live in `publicMetaData`, not in the tree, so a screen input created inside that
 * window is erased from memory and the next `saveScreen` persists
 * `publicJson.screenInputs: []` over it.
 *
 * Measured (session 25c5e8e8, `chat_1788361602558_0n00v6`): four screens whose
 * `ui_screen_input.create` answered `{ok: 1}` inside this window, all four empty on
 * the canvas afterwards. Doing the same creates by hand — seconds after the
 * navigate, i.e. outside the window — persists every time.
 *
 * The `openProjectCallback` hook already exists and already fires after
 * `loadProject`'s setState; it was only wired into `duplicateDesignerScreen`. Using
 * it here makes "navigate returned" mean "the screen is loaded", which is what every
 * navigate-then-write caller already assumed — and what six 200 ms sleeps in
 * `mcp-chat-server`'s pipeline-completion were approximating.
 */
async function loadScreenAndWaitForProject(
  sdInstance: ScreenDesignInstance,
  screenId: string,
  reload: boolean = false
): Promise<boolean> {
  const sd = getValues(sdInstance);
  // `loadScreen` early-returns when the canvas is already on the target and reload is
  // false: no project load starts, so no callback can ever fire. Waiting on one would
  // burn the whole deadline on the cheapest case.
  if (sd?.sdId === screenId && !reload) {
    return loadScreenAndWait(sdInstance, screenId, reload);
  }

  let resolveProjectLoaded: () => void = () => {};
  const projectLoaded = new Promise<void>((resolve) => {
    resolveProjectLoaded = resolve;
  });
  // Armed BEFORE the load so the two waits overlap on one deadline instead of
  // stacking two — they are both waiting for the same load to finish.
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  const settled = Promise.race([
    projectLoaded,
    new Promise<void>((resolve) => {
      settleTimer = setTimeout(resolve, SCREEN_LOAD_TIMEOUT_MS);
    })
  ]);

  const ready = await loadScreenAndWait(sdInstance, screenId, reload, () => resolveProjectLoaded());
  await settled;
  if (settleTimer !== undefined) clearTimeout(settleTimer);
  return ready;
}

// Force a fresh re-fetch + repaint of the screen the canvas is CURRENTLY on.
// Previously this called loadScreenAndWait with the default reload=false, which
// hit the loadScreen same-screen early-return and was a silent no-op (it never
// repainted). It now saves the current screen first (hash-guarded, so a no-op
// when nothing changed) and reloads with reload=true so openProject runs a
// fresh fetch -> setScreenDesign(LoadProject) -> renderFrame — the same clean
// path a full page reload uses. This is the deterministic repaint the post-build
// pipeline relies on after consolidating its per-screen navigations; the save
// keeps it non-destructive for any caller with unsaved in-memory edits.
export async function reloadDesignerScreen(
  sdInstance: ScreenDesignInstance
): Promise<{ error: string } | { success: true; message: string; ready: boolean }> {
  const sd = getValues(sdInstance);
  const screenId = sd?.sdId;
  if (!screenId) return { error: "No screen is currently loaded" };

  const screenName = sd.sdName;
  await sd.saveScreen?.(true);
  const ready = await loadScreenAndWaitForProject(sdInstance, screenId, /* reload */ true);

  return {
    success: true,
    message: `Reloaded screen "${screenName}"`,
    ready
  };
}

/**
 * `abortOnSaveFailure` — refuse to leave the current screen when its save was
 * REJECTED (409 collab/RowVersion, or any other failure). Reloading after a
 * rejected save replaces the in-memory tree with the server copy, silently
 * discarding everything built since the last successful save. The human-driven
 * flows leave this off: they already get a warning toast and must stay free to
 * navigate away. Programmatic callers (MCP) turn it ON — an agent has no eyes
 * on the toast, and a wipe reads to it as "the render did nothing".
 */
export async function openDesignerScreen(
  sdInstance: ScreenDesignInstance,
  screenId: string,
  abortOnSaveFailure: boolean = false
): Promise<{ error: string } | { success: true; screenId: string; screenName: string; ready: boolean }> {
  const sd = getValues(sdInstance);
  const screen = getScreenInfo(sd, screenId);
  if (!screen) return { error: `Screen "${screenId}" not found` };

  const persisted: boolean | undefined = await sd.saveScreen?.(true);
  if (abortOnSaveFailure && persisted === false) {
    return {
      error:
        `The current screen could not be saved, so navigating to "${screen.name}" was aborted — ` +
        `continuing would reload over the unsaved tree and discard it. This screen is usually ` +
        `locked by a live collaboration session: close its other editors (or set ` +
        `COLLAB_EDITING_ENABLED=false) and retry.`
    };
  }
  const ready = await loadScreenAndWaitForProject(sdInstance, screenId);

  return {
    success: true,
    screenId,
    screenName: screen.name,
    ready
  };
}

export async function createBlankDesignerScreen(
  sdInstance: ScreenDesignInstance,
  screenName: string
): Promise<ScreenAlreadyExistsError | { error: string } | { success: true; screenId: string; ready: boolean }> {
  const sd = getValues(sdInstance);
  const projectId = sd?.project?.id;
  if (!projectId) return { error: "No project loaded" };

  // Collision check runs against a FRESHLY FETCHED screen list, not the
  // in-memory React state. Without this refresh, a prior ui_screen.rename
  // within the same LLM turn sequence would not yet be visible here —
  // setScreenList's setState is async and a subsequent getValues() can
  // still see the pre-rename list. The create call then rejects with a
  // stale "already exists" error even though the old name was freed up
  // seconds ago.
  await sd.setScreenList?.();
  const refreshedSd = getValues(sdInstance);
  const collisionKey = normalizeScreenNameForCollision(screenName);
  const existingScreen = (refreshedSd.screenList || []).find(
    (s: IScreenDesignInfo) => normalizeScreenNameForCollision(s.name) === collisionKey
  );
  if (existingScreen) {
    return { error: buildScreenAlreadyExistsError(screenName, existingScreen.id), existingScreenId: existingScreen.id };
  }

  await sd.saveScreen?.(true);
  const res = await ScreenService.Create(buildBlankScreenRequest(projectId, screenName));
  if (!isSuccessfulResponse(res)) {
    return {
      error:
        `Failed to create screen${res?.status ? `: ${res.status}` : ""}` +
        (res?.status === 500
          ? ' — if this is a naming conflict with an existing backend resource, retry immediately with a different name (e.g. append "Alt" or "Screen" to the original name).'
          : "")
    };
  }

  const newScreenId = res.data?.screenId ?? res.data;
  await sd.setScreenList?.();
  const ready = await loadScreenAndWaitForProject(sdInstance, newScreenId);

  return {
    success: true,
    screenId: newScreenId,
    ready
  };
}

export interface MasterScreenLayoutInput {
  header?: ELayoutPosition;
  footer?: ELayoutPosition;
  left?: ELayoutPosition;
  right?: ELayoutPosition;
}

export async function createMasterDesignerScreen(
  sdInstance: ScreenDesignInstance,
  screenName: string,
  layout?: MasterScreenLayoutInput
): Promise<
  ScreenAlreadyExistsError | { error: string } | { success: true; screenId: string; ready: boolean; reused?: boolean }
> {
  const sd = getValues(sdInstance);
  const projectId = sd?.project?.id;
  if (!projectId) return { error: "No project loaded" };

  const collisionKey = normalizeScreenNameForCollision(screenName);
  const existingScreen = (sd.screenList || []).find(
    (s: IScreenDesignInfo) => normalizeScreenNameForCollision(s.name) === collisionKey
  );
  if (existingScreen) {
    // Idempotent reuse: when a master with this name ALREADY exists (typically
    // an orphan from a prior failed run, or a legit "move the menu to the master
    // screen" retry), reuse it — navigate in + re-apply the layout — instead of
    // erroring and letting the specialist spawn an orphan (the bug: Turn 2 of
    // session ff9450b2 bailed on this collision and created UntitledScreen2).
    // This mirrors the orphan-reuse policy master-shell.ts applies when a
    // master with the target name already exists (domain-derived names or
    // legacy "AppShell"). A NON-master name collision stays a genuine
    // conflict (you can't create a master on top of a regular screen).
    if (existingScreen.publicMetaData?.screenType === EScreenType.Master) {
      const reusedReady = await loadScreenAndWaitForProject(sdInstance, existingScreen.id);
      if (layout && reusedReady) {
        const refreshedSd = getValues(sdInstance);
        refreshedSd.setLayoutPosition?.(
          layout.header ?? ELayoutPosition.invisible,
          layout.footer ?? ELayoutPosition.invisible,
          layout.left ?? ELayoutPosition.invisible,
          layout.right ?? ELayoutPosition.invisible
        );
      }
      return { success: true, screenId: existingScreen.id, ready: reusedReady, reused: true };
    }
    return { error: buildScreenAlreadyExistsError(screenName, existingScreen.id), existingScreenId: existingScreen.id };
  }

  await sd.saveScreen?.(true);
  const res = await ScreenService.Create(buildMasterScreenRequest(projectId, screenName));
  if (!isSuccessfulResponse(res)) {
    return { error: `Failed to create master screen${res?.status ? `: ${res.status}` : ""}` };
  }

  const newScreenId = res.data?.screenId ?? res.data;
  await sd.setScreenList?.();
  const ready = await loadScreenAndWaitForProject(sdInstance, newScreenId);

  // Apply layout positions if provided
  if (layout && ready) {
    const refreshedSd = getValues(sdInstance);
    refreshedSd.setLayoutPosition?.(
      layout.header ?? ELayoutPosition.invisible,
      layout.footer ?? ELayoutPosition.invisible,
      layout.left ?? ELayoutPosition.invisible,
      layout.right ?? ELayoutPosition.invisible
    );
  }

  return {
    success: true,
    screenId: newScreenId,
    ready
  };
}

export async function duplicateDesignerScreen(
  sdInstance: ScreenDesignInstance,
  screenId: string
): Promise<{ error: string } | { success: true; sourceScreenName: string; screenId: string; ready: boolean }> {
  const sd = getValues(sdInstance);
  const screen = getScreenInfo(sd, screenId);
  if (!screen) return { error: `Screen "${screenId}" not found` };

  await sd.saveScreen?.(true);
  const res = await ScreenService.DuplicateScreen(screenId);
  if (!isSuccessfulResponse(res)) {
    return { error: `Failed to duplicate screen${res?.status ? `: ${res.status}` : ""}` };
  }

  const newScreenId = res.data?.screenId ?? res.data;
  syncDuplicatedScreenCanvasSize(sd, screenId, newScreenId);
  syncDuplicatedScreenFolder(sd, screenId, newScreenId);
  await sd.setScreenList?.();

  // Capture the source's StartProcess state BEFORE the load swaps state.project.
  const sourceTriggers = sdInstance.state.project?.startProcessTriggers;
  const sourceHadStartProcess =
    !!sourceTriggers && Object.values(sourceTriggers).some((list) => list?.some((t) => t.sourceId === screenId));

  // openProject loads the project inside a non-awaited processWithSpinner, so its
  // loadProject setState({ project }) lands AFTER loadScreenAndWait's screen-ready
  // signal. If we re-indexed before that, the late project setState would clobber the
  // freshly-written triggers. Wait for openProject's callback (fires after loadProject)
  // so syncGeneratedScreen runs last. Duplicate always loads a NEW screen id, so
  // openProject is always invoked and the callback always fires; the timeout is a
  // belt-and-suspenders guard against any path where it doesn't.
  let resolveProjectLoaded = () => {};
  const projectLoaded = new Promise<void>((resolve) => {
    resolveProjectLoaded = resolve;
  });
  const ready = await loadScreenAndWait(sdInstance, newScreenId, false, () => resolveProjectLoaded());

  // Backend copies the source screen's action tree onto the new screen; the
  // local startProcessTriggers reverse-index must be extended with entries
  // pointing at the new screen id, otherwise the wizard will report the
  // duplicated screen's StartProcess actions as untriggered.
  if (ready && sourceHadStartProcess) {
    await Promise.race([projectLoaded, new Promise<void>((resolve) => setTimeout(resolve, 10000))]);
    const duplicatedScreen = sdInstance.screenProvider.getScreenDesign?.();
    if (duplicatedScreen?.id === newScreenId) {
      sdInstance.startProcessTriggerSync.syncGeneratedScreen(newScreenId, duplicatedScreen.displayItems);
    }
  }

  return {
    success: true,
    sourceScreenName: screen.name,
    screenId: newScreenId,
    ready
  };
}

export async function deleteDesignerScreen(
  sdInstance: ScreenDesignInstance,
  screenId: string
): Promise<{ error: string } | { success: true; screenName: string; nextScreenId?: string; ready?: boolean }> {
  const sd = getValues(sdInstance);
  const screen = getScreenInfo(sd, screenId);
  if (!screen) return { error: `Screen "${screenId}" not found` };

  const deletingCurrentScreen = String(sd.sdId) === String(screenId);
  const res = deletingCurrentScreen
    ? await ScreenService.DeleteCurrentScreen(screenId.toString())
    : await ScreenService.Delete(screenId.toString());

  if (!isSuccessfulResponse(res)) {
    return { error: `Failed to delete screen${res?.status ? `: ${res.status}` : ""}` };
  }

  // Drop the deleted screen's StartProcess triggers from the reverse index. The UI
  // delete path (screens-window) prunes itself; this covers MCP/AI screen deletion,
  // which funnels through here instead.
  sdInstance.startProcessTriggerSync.pruneScreen(screenId.toString());

  // KOA-18199: also prune the workflow step↔screen mapping, mirroring screens-window's
  // UI delete path — an MCP/AI delete used to leave a stale processStepScreenIds entry,
  // which hid the "assigned screen no longer exists" diagram warning and let publish
  // validation pass a broken workflow.
  const project = sd.getProject?.();
  if (project?.processStepScreenIds?.some((pss) => String(pss.screenId) === String(screenId))) {
    project.processStepScreenIds = project.processStepScreenIds.filter(
      (pss) => String(pss.screenId) !== String(screenId)
    );
    await sd.updateProject?.(project);
  }

  await sd.setScreenList?.();

  if (deletingCurrentScreen) {
    const nextScreenId = res.data?.screenId ?? res.data;
    if (nextScreenId) {
      const ready = await loadScreenAndWaitForProject(sdInstance, nextScreenId);
      return {
        success: true,
        screenName: screen.name,
        nextScreenId,
        ready
      };
    }
  }

  return {
    success: true,
    screenName: screen.name
  };
}
